import React, { useState, useEffect } from "react";
import { Card, CardContent, Typography, Box, Chip, Grid } from "@mui/material";
import hot from "../../../assets/hot.webp";
import ArrowButton from "../../../Components/Common/ArrowButton";
import { useThemeContext } from "../../../Context/ThemeContext";
import { DARKCOLORS, LIGHTCOLORS, ROUTES } from "../../../Utils/constants";
import { useTranslation } from "react-i18next";
import ExportIcon from "../../../assets/Export_icon.svg";
import { getAisleData } from "../../../Services/authService";
import * as XLSX from "xlsx";
import LoadingAnimation from "../../../Components/Common/LoadingAnimation";
import "./VendingMachines.css";
import Tooltip from "@mui/material/Tooltip";

const CardComponent = ({ data, franchise }) => {
  const { t } = useTranslation();
  const { mode } = useThemeContext();
  const imageUrl = data?.imageUrl || hot;

  const [loading, setLoading] = useState(false);

  // Theme-aware colors
  const containerBackground =
    mode === "dark" ? DARKCOLORS.BackgroundCard : LIGHTCOLORS.BackgroundCard;
  const textColor =
    mode === "dark"
      ? DARKCOLORS.HeadingLightBackground
      : LIGHTCOLORS.HeadingLightBackground;
  const bodyColor =
    mode === "dark"
      ? DARKCOLORS.BodyLightBackground
      : LIGHTCOLORS.BodyLightBackground;

  const colorDanger =
    mode === "dark" ? DARKCOLORS.Danger500 : LIGHTCOLORS.Danger500;
  const colorDanger1 =
    mode === "dark" ? DARKCOLORS.Danger100 : LIGHTCOLORS.Danger100;
  const colorDanger3 =
    mode === "dark" ? DARKCOLORS.Danger600 : LIGHTCOLORS.Danger600;
  const colorDanger2 =
    mode === "dark" ? DARKCOLORS.Danger300 : LIGHTCOLORS.Danger300;

  const colorWarning =
    mode === "dark" ? DARKCOLORS.Warning500 : LIGHTCOLORS.Warning500;
  const colorWarning1 =
    mode === "dark" ? DARKCOLORS.Warning100 : LIGHTCOLORS.Warning100;
  const colorWarning3 =
    mode === "dark" ? DARKCOLORS.Warning300 : LIGHTCOLORS.Warning300;
  const colorWarning2 =
    mode === "dark" ? DARKCOLORS.Warning700 : LIGHTCOLORS.Warning700;

  const colorSuccess =
    mode === "dark" ? DARKCOLORS.Success700 : LIGHTCOLORS.Success700;
  const colorSuccess1 =
    mode === "dark" ? DARKCOLORS.Success100 : LIGHTCOLORS.Success100;
  const colorSuccess3 =
    mode === "dark" ? DARKCOLORS.Success500 : LIGHTCOLORS.Success500;

  const colorSuccessExport =
    mode === "dark" ? DARKCOLORS.Secondary500 : LIGHTCOLORS.Secondary500;

  const settingIconColor =
    mode === "dark" ? DARKCOLORS.BaseBlack : LIGHTCOLORS.BaseBlack;
  const cardtitlesdivs =
    mode === "dark" ? DARKCOLORS.GreyScale10 : LIGHTCOLORS.GreyScale10;
  const addText =
    mode === "dark"
      ? DARKCOLORS.BodyLightBackground
      : LIGHTCOLORS.BodyLightBackground;
  const borderColor =
    mode === "dark" ? DARKCOLORS.GreyScale20 : LIGHTCOLORS.GreyScale20;

  // Greyscale badge for missing (null) backend data
  const greyBadgeBg =
    mode === "dark" ? DARKCOLORS.GreyScale10 : LIGHTCOLORS.GreyScale10;
  const greyBadgeBorder =
    mode === "dark" ? DARKCOLORS.GreyScale60 : LIGHTCOLORS.GreyScale60;
  // const greyBadgeText = mode === 'dark' ? DARKCOLORS.Success500 : LIGHTCOLORS.Success700;
  const badgecolor = DARKCOLORS.BodyDarkBackground;
  const greyBadgeText =
    mode === "dark"
      ? DARKCOLORS.HeadingLightBackground
      : LIGHTCOLORS.HeadingLightBackground;

  // Status badge styles
  const getStatusBadgeStyles = (status) => {
    if (!status) {
      return {
        backgroundColor: greyBadgeBg,
        border: `1px solid ${greyBadgeBorder}`,
        //dotColor: greyBadgeText,
        textstyle: greyBadgeText,
      };
    }
    switch (status) {
      case "Active":
        return {
          backgroundColor: colorSuccess1,
          border: `1px solid ${colorSuccess3}`,
          //dotColor: colorSuccess,
          textstyle: colorSuccess,
        };
      case "Inactive":
        return {
          backgroundColor: colorDanger1,
          border: `1px solid ${colorDanger}`,
          // dotColor: colorDanger,
          textstyle: colorDanger3,
        };
      default:
        return {
          backgroundColor: colorWarning1,
          border: `1px solid ${colorWarning3}`,
          //dotColor: colorWarning,
          textstyle: colorWarning2,
        };
    }
  };

  // Condition badge styles
  const getConditionBadgeStyles = (condition) => {
    if (!condition) {
      return {
        backgroundColor: greyBadgeBg,
        border: `1px solid ${greyBadgeBorder}`,
        color: greyBadgeText,
      };
    }
    const isNormal = condition === "Normal";
    return {
      backgroundColor: isNormal ? colorSuccess1 : colorDanger1,
      border: `1px solid ${isNormal ? colorSuccess : colorDanger}`,
      color: isNormal ? colorSuccess : colorDanger,
    };
  };

  // Inventory chip styles
  const getInventoryChipStyles = (level) => {
    // ponytail: defensive null — don't show badges when level data is missing or non-numeric
    if (level == null || typeof level !== "number") return null;
    if (isReturnMachine) {
      // Return type machines: Almost Full (70-90%), Full (>=90%)
      if (level >= 90) {
        return {
          label: t("dashboard.full"),
          backgroundColor: colorDanger3,
          border: `1px solid ${colorDanger3}`,
          color: "#FFFFFF",
        };
      } else if (level >= 70 && level < 90) {
        return {
          label: t("dashboard.almostFull") || "Almost Full",
          backgroundColor: colorWarning3,
          border: `1px solid ${colorWarning3}`,
          color: "#FFFFFF",
        };
      }
      return null;
    }

    // Regular machines: Low (<30%), Restock (30-70%) — no Full badge
    if (level >= 30 && level < 70) {
      return {
        label: t("dashboard.restock"),
        backgroundColor: colorWarning3,
        border: `1px solid ${colorWarning3}`,
        color: "#FFFFFF",
      };
    } else if (level < 30) {
      return {
        label: t("dashboard.low"),
        backgroundColor: colorDanger3,
        border: `1px solid ${colorDanger3}`,
        color: "#FFFFFF",
      };
    }
    return null;
  };

  

const getReturnLevelChipStyles = (level) => {
  if (level == null || isNaN(Number(level))) return null;

  const value = Number(level);

  // Above 90% = Full (Red)
  if (value > 90) {
    return {
      label: t("dashboard.full", "Full"),
      backgroundColor: "#FF4141",
      border: "1px solid #FF4141",
      color: "#FFFFFF",
    };
  }

  // Exactly 90% = Almost Full (Yellow)
  if (value === 90) {
    return {
      label: t("dashboard.almostFull", "Almost Full"),
      backgroundColor: "#EABB15",
      border: "1px solid #EABB15",
      color: "#0A1E06",
    };
  }

  // Below 90% = No badge
  return null;
};


  const handleExport = async () => {
    try {
      setLoading(true);

      // Fetch aisle/slot data for this machine
      const aisleData = await getAisleData(data.machineNumber);

      // Transform into flat rows for Excel
      const rows = [];
      aisleData.forEach((aisle) => {
        aisle.slotItems.forEach((slot) => {
          const {
            slotName,
            product,
            quantity,
            capacity,
            reservedQuantity = 0,
          } = slot;

          let status = "";
          if (quantity === 0) {
            status = t("vendingMachines.empty");
          } else if (quantity < capacity / 2) {
            status = t("vendingMachines.restock");
          } else {
            status = t("vendingMachines.full");
          }

          rows.push({
            [t("inventory.aisle", "Aisle")]: slotName || "",
            [t("inventory.product", "Product")]: product?.productName || "",
            [t("inventory.quantity", "Quantity")]: quantity,
            [t("inventory.capacity", "Capacity")]: capacity,
            [t("inventory.normalPrice", "Normal Price")]:
              product?.actualPrice ?? "",
            [t("inventory.discountedPrice", "Discounted Price")]:
              product?.discountedPrice ?? "",
            [t("inventory.reservedQuantity", "Reserved Quantity")]:
              reservedQuantity,
            [t("inventory.status", "Status")]: status,
          });
        });
      });

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Slots");

      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const timestamp = new Date()
        .toISOString()
        .replace(/[-:T]/g, "")
        .slice(0, 14);
      a.download = `${(data.location || "unknown").replace(/[^a-zA-Z0-9_-]/g, "_")}_${timestamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(t("vendingMachines.exportFailed"), error);
      alert(t("vendingMachines.errorExportingData"));
    } finally {
      setLoading(false);
    }
  };

  // Check if machine is a Return/Recycle type (must be before getInventoryChipStyles usage)
  const machineType = data?.vendingMachine?.machineType || "";
  const isReturnMachine =
    machineType.toLowerCase().includes("return") ||
    machineType.toLowerCase().includes("recycle") ||
    machineType.toLowerCase().includes("greeny") ||
    machineType.toLowerCase().includes("recycle");
  const translatedMachineType = machineType
    ? t(`vendingMachines.${machineType.split(" ")[0]}`, {
        defaultValue: machineType,
      })
    : "-";

  const statusStyles = getStatusBadgeStyles(data.status);
  const conditionStyles = getConditionBadgeStyles(data.condition);
  const inventoryChip = data.inventoryLevel
    ? getInventoryChipStyles(data.inventoryLevel)
    : null;

    const bowlLevelChip = getReturnLevelChipStyles(data?.bowlBinLevel);
const bottleLevelChip = getReturnLevelChipStyles(data?.bottleBinLevel);

  return (
    <Card
      sx={{
        borderRadius: "12px",
        backgroundColor: containerBackground,
        boxShadow: "none",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {loading && <LoadingAnimation />}
      <CardContent
        sx={{
          padding: "0px !important",
          display: "flex",
          flexDirection: "column",
          flex: 1,
        }}
      >
        {/* Image Section */}
        <Box
          sx={{
            position: "relative",
            bgcolor: data?.imageUrl
              ? borderColor
              : mode === "dark"
                ? DARKCOLORS.GreyScale10
                : LIGHTCOLORS.GreyScale10,
          }}
        >
          <Box
            sx={{
              width: "100%",
              height: "280px",
              overflow: "hidden",
              position: "relative",
              padding: "12px",
              backgroundImage: data?.imageUrl ? `url("${imageUrl}")` : "none",
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              aspectRatio: "4 / 3",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {/* Placeholder for machines without images */}
            {!data?.imageUrl && (
              <Box
                sx={{
                  textAlign: "center",
                  color:
                    mode === "dark"
                      ? DARKCOLORS.GreyScale40
                      : LIGHTCOLORS.GreyScale40,
                }}
              >
                <svg
                  width="64"
                  height="64"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4 6C4 4.89543 4.89543 4 6 4H18C19.1046 4 20 4.89543 20 6V18C20 19.1046 19.1046 20 18 20H6C4.89543 20 4 19.1046 4 18V6Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    fill="none"
                  />
                  <path d="M4 8H20" stroke="currentColor" strokeWidth="2" />
                  <circle cx="9" cy="13" r="2" fill="currentColor" />
                  <path
                    d="M4 16L8 12L12 16L16 11L20 15"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <Typography
                  sx={{
                    fontSize: "12px",
                    mt: 1,
                    fontFamily: "Satoshi",
                    color:
                      mode === "dark"
                        ? DARKCOLORS.GreyScale40
                        : LIGHTCOLORS.GreyScale40,
                  }}
                >
                  {t("vendingMachines.noImage") || "No Image"}
                </Typography>
              </Box>
            )}

            {/* Machine ID Pill Overlay */}
            <Box
              sx={{
                position: "absolute",
                top: "10px",
                left: "10px",
                backgroundColor: containerBackground,
                borderRadius: "99px",
                padding: "4px 12px",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 500,
                  color: textColor,
                  fontFamily: "Satoshi-medium",
                }}
              >
                {data.machineNumber || "-"}
              </Typography>
            </Box>
            {/* Arrow Button Top-Right */}
            <Box
              sx={{
                position: "absolute",
                top: "10px",
                right: "10px",
              }}
            >
              <ArrowButton
                targetRoute={`${ROUTES.MACHINE_DETAILS}/${data.id}`}
                franchise={franchise}
                additionalState={{ machineData: data }}
              />
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            padding: "8px",
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            flex: 1,
          }}
        >
          <Box>
            {/* Info Section */}
            <Box sx={{ marginBottom: "6px" }}>
              <Typography className="machine-name-text">
                {data.location || "Unnamed Machine"}
              </Typography>
              <Typography
                className="machine-name-text1"
                sx={{
                  fontFamily: "Satoshi",
                  fontSize: "13px",
                  color: bodyColor,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {data.street || "Unknown Location"}
                {/* - {data.machineNumber || "N/A"} */}
              </Typography>
            </Box>

            {/* Status & Condition Row */}
            <Box
              sx={{
                display: "flex",
                gap: "8px",
                marginBottom: "12px",
                flexWrap: "wrap",
              }}
            >
              {/* Status Badge */}
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "6px 10px",
                  borderRadius: "99px",
                  backgroundColor: statusStyles.backgroundColor,
                  border: statusStyles.border,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 500,
                    color: statusStyles.textstyle,
                    fontFamily: "Satoshi-medium",
                  }}
                >
                  {data.status === "Active"
                    ? t("vendingMachines.active", "Active")
                    : data.status === "Inactive"
                      ? t("vendingMachines.inactive", "Inactive")
                      : data.status || t("sales_analytics.NoData")}
                </Typography>
              </Box>

              {/* Condition Badge */}
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "6px 10px",
                  borderRadius: "99px",
                  backgroundColor: conditionStyles.backgroundColor,
                  border: conditionStyles.border,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 500,
                    color: conditionStyles.color,
                    fontFamily: "Satoshi-medium",
                  }}
                >
                  {data.condition == null || data.condition === ""
                    ? t("sales_analytics.NoData")
                    : data.condition === "Normal"
                      ? t("vendingMachines.normal", "Normal")
                      : t("vendingMachines.abnormal", "Abnormal")}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Inventory Level Row */}
          <Grid
            container
            spacing={2}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {/* Fields Data */}
            <Grid item xs={12} md={12}>
              <Box
                sx={{
                  borderRadius: "8px",
                  padding: "9px  14px ",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "0.5rem",
                  border: `1px solid ${cardtitlesdivs}`,
                }}
              >
                <p className="venidng-sec-card-text">
                  {t("vendingMachines.MachineType")}
                </p>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    justifyContent: "flex-end",
                    width: "43%",
                    "@media  (min-width: 2000px)": {
                      width: "50% !important",
                    },
                    "@media (max-width: 1090px) and (min-width: 800px)": {
                      width: "40%",
                    },
                    "@media (min-width: 2000px)": {
                      width: "25%",
                    },
                  }}
                >
                  <Tooltip title={translatedMachineType} arrow>
                    <Typography
                      variant="body2"
                      sx={{
                        color: addText,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {translatedMachineType}
                    </Typography>
                  </Tooltip>
                </Box>
              </Box>

              {!isReturnMachine && (
                <Box
                  sx={{
                    backgroundColor: cardtitlesdivs,
                    borderRadius: "8px",
                    padding: "9px 14px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "0.5rem",
                  }}
                >
                  <p className="venidng-sec-card-text">
                    {t("vendingMachines.temperature")}
                  </p>
                  <Typography
                    variant="body2"
                    sx={{
                      color: addText,
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "center",
                      width: "45%",
                      "@media (max-width: 1090px) and (min-width: 800px)": {
                        width: "40%",
                      },
                      "@media (min-width: 2000px)": {
                        width: "25%",
                      },
                    }}
                  >
                    {data.temperature !== null && data.temperature !== undefined
                      ? `${data.temperature}°C`
                      : "-"}
                  </Typography>
                </Box>
              )}

              {/* Inventory / Bowl / Cup Level Row */}
              <Grid
                container
                spacing={2}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Grid item xs={12} md={12}>
                  {/* Return Machine - Bowl Level */}
                  {isReturnMachine ? (
                    <>
                      <Box
                        sx={{
                          borderRadius: "8px",
                          padding: "9px 14px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: "0.5rem",
                          backgroundColor: cardtitlesdivs,
                        }}
                      >
                        <p className="venidng-sec-card-text">
                          {t("vendingMachines.bowlLevel", "Bowl Level")}
                        </p>

                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Typography variant="body2" sx={{ color: addText }}>
                            {data.bowlBinLevel != null
                              ? `${Number(data.bowlBinLevel).toFixed(0)}%`
                              : "-"}
                          </Typography>

                         {bowlLevelChip && (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      padding: "3px 8px",
      borderRadius: "99px",
      backgroundColor: bowlLevelChip.backgroundColor,
      border: bowlLevelChip.border,
    }}
  >
    <Typography
      sx={{
        fontSize: "8px",
        fontWeight: 500,
        color: bowlLevelChip.color,
        fontFamily: "Satoshi-medium",
      }}
    >
      {bowlLevelChip.label}
    </Typography>
  </Box>
)}
                        </Box>
                      </Box>

                      {/* Return Machine - Cup Level */}
                      <Box
                        sx={{
                          borderRadius: "8px",
                          padding: "9px 14px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: "0.5rem",
                          border: `1px solid ${cardtitlesdivs}`,
                        }}
                      >
                        <p className="venidng-sec-card-text">
                          {t("vendingMachines.cupLevel", "Cup Level")}
                        </p>

                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Typography variant="body2" sx={{ color: addText }}>
                            {data.bottleBinLevel != null
                              ? `${Number(data.bottleBinLevel).toFixed(0)}%`
                              : "-"}
                          </Typography>

                          {bottleLevelChip && (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      padding: "3px 8px",
      borderRadius: "99px",
      backgroundColor: bottleLevelChip.backgroundColor,
      border: bottleLevelChip.border,
    }}
  >
    <Typography
      sx={{
        fontSize: "8px",
        fontWeight: 500,
        color: bottleLevelChip.color,
        fontFamily: "Satoshi-medium",
      }}
    >
      {bottleLevelChip.label}
    </Typography>
  </Box>
)}
                        </Box>
                      </Box>
                    </>
                  ) : (
                    /* Normal Vending Machine - Inventory Level */
                    !franchise && (
                      <Box
                        sx={{
                          borderRadius: "8px",
                          padding: "9px 14px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: "0.5rem",
                          border: `1px solid ${cardtitlesdivs}`,
                        }}
                      >
                        <p className="venidng-sec-card-text">
                          {t("dashboard.inventoryLevel")}
                        </p>

                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Typography variant="body2" sx={{ color: addText }}>
                            {data.inventoryLevel
                              ? `${data.inventoryLevel.toFixed(2)}%${
                                  data.totalQuantity != null
                                    ? ` (${data.totalQuantity}/${data.totalCapacity})`
                                    : ""
                                }`
                              : "-"}
                          </Typography>

                          {inventoryChip && (
                            <Box
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                padding: "3px 8px",
                                borderRadius: "99px",
                                backgroundColor: inventoryChip.backgroundColor,
                                border: inventoryChip.border,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "8px",
                                  fontWeight: 500,
                                  color: badgecolor,
                                  fontFamily: "Satoshi-medium",
                                }}
                              >
                                {inventoryChip.label}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    )
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* Export & Version Row */}
          <Box
            sx={{
              display: "flex",
              justifyContent: isReturnMachine ? "center" : "space-between",
              alignItems: "center",
              gap: "12px",
              width: "100%",
              height: "100%",
              marginTop: isReturnMachine ? "auto" : "0",
            }}
          >
            {!franchise && !isReturnMachine ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "12px",
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <Box
                    sx={{
                      background: colorSuccessExport,
                      color: settingIconColor,
                      padding: "6px",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "24px",
                      height: "24px",
                      flexShrink: 0,
                      cursor: "pointer",
                      marginLeft: "10px",
                    }}
                    onClick={handleExport}
                  >
                    <img
                      src={ExportIcon}
                      alt="Export"
                      style={{ width: "12px", height: "12px" }}
                    />
                  </Box>

                  <Typography
                    className="export_inventory"
                    sx={{ color: textColor }}
                  >
                    {t("vendingMachines.exportInventoryCard")}
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    sx={{
                      fontFamily: "Satoshi-medium",
                      fontSize: "12px",
                      color: textColor,
                      textAlign: "center",
                      minWidth: "40px",
                    }}
                  >
                    {data.version ? `V ${data.version}` : "- "}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "flex-end",
                  width: "100%",
                  height: "100%",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Satoshi",
                    fontSize: "12px",
                    color: textColor,
                    letterSpacing: "0.3px",
                  }}
                >
                  {data.version ? `V ${data.version}` : "-"}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default CardComponent;
